import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { Permission } from '../common/enums/permissions';
import { AuthGuard } from '../core/authentication/auth.guard';
import { PermissionGuard } from '../core/authentication/permission.guard';
import { AgreementEmploymentTypeGuard } from '../shared/guards/agreement-employment-type.guard';
import { AgreementFormComponent } from './agreement-form/agreement-form.component';
import { ApproveAgreementVoidingComponent } from './approve-agreement-voiding/approve-agreement-voiding.component';
import { MultipleAgreementFormComponent } from './multiple-agreement-form/multiple-agreement-form.component';
import { OldWorkerAgreementRouteGuard } from './old-worker-agreement-route.guard';
import { SuccessfullyApprovedAgreementComponent } from './successfully-approved-agreement/successfully-approved-agreement.component';
import { SuccessfullySendToApprovalComponent } from './successfully-send-to-approval/successfully-send-to-approval.component';
import { WorkerAgreementDetailsComponent } from './worker-agreement-details/worker-agreement-details.component';
import { WorkerAgreementListTabsContainerComponent } from './worker-agreement-list-tabs-container/worker-agreement-list-tabs-container.component';
import { SelfGuard } from '../shared/guards/self-guard';
import { AgreementAnnexComponent } from './agreement-annex/agreement-annex.component';
import { AnnexSignatureComponent } from './agreement-annex/annex-signature/annex-signature.component';
import { SuccessfullySignedAnnexComponent } from './agreement-annex/annex-signature/successfully-signed-annex/successfully-signed-annex.component';
import { SuccessfullySentAnnexToApprovalComponent } from './agreement-annex/successfully-sent-annex-to-approval/successfully-sent-annex-to-approval.component';
import { ModuleName } from '../subscription-package';
import { SuccessfullyCreatedAnnexComponent } from './agreement-annex/successfully-created-annex/successfully-created-annex.component';
import { AnnexDetailsComponent } from './annex-details/annex-details.component';

const routes: Routes = [
  {
    path: 'approveAgreementVoiding',
    component: ApproveAgreementVoidingComponent,
  },
  {
    path: 'workers/multiple/employmentType/2/agreements',
    component: MultipleAgreementFormComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissions: [Permission.ManageAllExternalWorkersList, Permission.ManageMyExternalWorkersList, Permission.ViewMyExternalWorker] },
  },
  {
    path: 'workers/:workerId/employmentType/2/agreements',
    component: AgreementFormComponent,
    canActivate: [AuthGuard, PermissionGuard, AgreementEmploymentTypeGuard, SelfGuard],
    data: { permissions: [Permission.ManageAllExternalWorkersList, Permission.ManageMyExternalWorkersList, Permission.ViewMyExternalWorker] },
  },
  {
    path: 'workers/:workerId/employmentType/2/agreements/:agreementId',
    component: AgreementFormComponent,
    canActivate: [AuthGuard, PermissionGuard, AgreementEmploymentTypeGuard, SelfGuard],
    data: { permissions: [Permission.ManageAllExternalWorkersList, Permission.ManageMyExternalWorkersList, Permission.ViewMyExternalWorker] },
  },
  {
    path: 'workers/multiple/employmentType/6/agreements',
    component: MultipleAgreementFormComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissions: [Permission.ManageAllExternalWorkersList, Permission.ManageMyExternalWorkersList, Permission.ViewMyExternalWorker] },
  },
  {
    path: 'workers/:workerId/employmentType/6/agreements',
    component: AgreementFormComponent,
    canActivate: [AuthGuard, PermissionGuard, AgreementEmploymentTypeGuard, SelfGuard],
    data: { permissions: [Permission.ManageAllExternalWorkersList, Permission.ManageMyExternalWorkersList, Permission.ViewMyExternalWorker] },
  },
  {
    path: 'workers/:workerId/employmentType/6/agreements/:agreementId',
    component: AgreementFormComponent,
    canActivate: [AuthGuard, PermissionGuard, AgreementEmploymentTypeGuard, SelfGuard],
    data: { permissions: [Permission.ManageAllExternalWorkersList, Permission.ManageMyExternalWorkersList, Permission.ViewMyExternalWorker] },
  },
  {
    path: 'workerAgreementDetails/:agreementId',
    component: WorkerAgreementDetailsComponent,
    canActivate: [AuthGuard, PermissionGuard, OldWorkerAgreementRouteGuard],
    data: {
      permissions: [Permission.AgreementDetails]
    }
  },
  {
    path: 'workers/:workerId/employmentType/2/agreements/:agreementId/details',
    component: WorkerAgreementDetailsComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'workers/:workerId/employmentType/6/agreements/:agreementId/details',
    component: WorkerAgreementDetailsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'workerAgreements',
    component: WorkerAgreementListTabsContainerComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      permissions: [Permission.ViewWorkerAgreementsList, Permission.Supervisor],
    },
  },
  { path: 'successfullyCreatedAnnex/:annexId', component: SuccessfullyCreatedAnnexComponent, canActivate: [AuthGuard] },
  { path: 'successfullyApprovedAgreement/:id', component: SuccessfullyApprovedAgreementComponent, canActivate: [AuthGuard] },
  { path: 'successfullySendToApproval/:id', component: SuccessfullySendToApprovalComponent, canActivate: [AuthGuard] },
  {
    path: 'workers/:workerId/employmentType/:employmentTypeId/agreements/:agreementId/annex',
    component: AgreementAnnexComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      permissions: [Permission.CreateAnnex, Permission.ManageAllExternalWorkersList, Permission.ManageMyExternalWorkersList, Permission.ViewMyExternalWorker],
      moduleName: ModuleName.Annex
    }
  },
  {
    path: 'workers/:workerId/employmentType/:employmentTypeId/agreements/:agreementId/annexes/:annexId/signature',
    component: AnnexSignatureComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      permissions: [
        Permission.CreateAnnex, 
        Permission.ManageAllExternalWorkersList, 
        Permission.ManageMyExternalWorkersList, 
        Permission.ViewMyExternalWorker],
      moduleName: ModuleName.Annex
    }
  },
  {
    path: 'workers/:workerId/annexes/successfully-signed',
    component: SuccessfullySignedAnnexComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'workers/:workerId/annexes/successfully-sent-to-approval',
    component: SuccessfullySentAnnexToApprovalComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'workers/:workerId/employmentType/:employmentTypeId/agreements/:agreementId/annexes/:annexId/details',
    component: AnnexDetailsComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      permissions: [
        Permission.ViewAllExternalWorkers,
        Permission.ManageMyExternalWorkersList,
        Permission.ViewMyExternalWorker,
        Permission.ManageAsAExternalWorker,
      ],
    },
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class WorkerAgreementsRoutingModule { }
